import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import {
  publicContractsApi,
  SigningContextResponse,
  SignatureCompletedResponse,
  SubmitSignatureRequest,
} from "../../api/publicContractsApi";

type ScreenStep = "disclosure" | "preview" | "sign";

type LoadState =
  | { kind: "loading" }
  | {
      kind: "ready";
      ctx: SigningContextResponse;
      step: ScreenStep;
    }
  | {
      kind: "completed";
      ctx: SigningContextResponse;
      result: SignatureCompletedResponse;
    }
  | { kind: "not-found" }
  | { kind: "gone" }
  | { kind: "already-signed" }
  | { kind: "error"; message: string };

// Great Vibes is registered globally via @font-face in src/index.css.
// Local /fonts/GreatVibes-Regular.ttf is the primary src; Google Fonts gstatic
// woff2 is the fallback. No runtime <link> injection needed.

const SignContractPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "not-found" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ctx = await publicContractsApi.getSigningContext(token);
        if (cancelled) return;
        if (ctx.alreadySigned) {
          setState({ kind: "already-signed" });
          return;
        }
        if (ctx.voided || ctx.expired) {
          setState({ kind: "gone" });
          return;
        }
        setState({ kind: "ready", ctx, step: "disclosure" });
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setState({ kind: "not-found" });
        else if (status === 410) setState({ kind: "gone" });
        else if (status === 409) setState({ kind: "already-signed" });
        else
          setState({
            kind: "error",
            message:
              err?.message ?? "Something went wrong loading this agreement.",
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const businessName =
    state.kind === "ready" || state.kind === "completed"
      ? state.ctx.operatorBusinessName
      : "";
  const clientName =
    state.kind === "ready" || state.kind === "completed"
      ? state.ctx.clientFullName
      : "";

  const goToStep = (step: ScreenStep) =>
    setState((prev) => (prev.kind === "ready" ? { ...prev, step } : prev));

  const onTerminal = (kind: "gone" | "already-signed") =>
    setState({ kind });

  const onSuccess = (result: SignatureCompletedResponse) =>
    setState((prev) =>
      prev.kind === "ready"
        ? { kind: "completed", ctx: prev.ctx, result }
        : prev
    );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto w-full max-w-3xl px-5 py-5 sm:py-6">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
            {businessName || "Service Agreement"}
          </h1>
          {clientName && (
            <p className="mt-1 text-sm text-slate-500">
              Prepared for {clientName}
            </p>
          )}
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-5 py-6 sm:py-10">
          {state.kind === "loading" && <LoadingPanel />}
          {state.kind === "not-found" && (
            <TerminalPanel
              title="Signing link not found"
              body="We couldn't find this signing link. Please check the link in your email."
            />
          )}
          {state.kind === "gone" && (
            <TerminalPanel
              title="Signing link no longer valid"
              body={`This signing link is no longer valid (voided or expired). Please contact ${
                businessName || "the company"
              } for a new link.`}
            />
          )}
          {state.kind === "already-signed" && (
            <TerminalPanel
              title="Already signed"
              body="This agreement has already been signed."
            />
          )}
          {state.kind === "error" && (
            <TerminalPanel title="Something went wrong" body={state.message} />
          )}
          {state.kind === "ready" && (
            <SigningFlow
              ctx={state.ctx}
              step={state.step}
              token={token!}
              onStepChange={goToStep}
              onTerminal={onTerminal}
              onSuccess={onSuccess}
            />
          )}
          {state.kind === "completed" && (
            <CompletedPanel
              ctx={state.ctx}
              result={state.result}
              token={token!}
            />
          )}
        </div>
      </main>
    </div>
  );
};

// ---------------------- shared panels ----------------------

const LoadingPanel: React.FC = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
    <p className="text-sm text-slate-500">Loading your agreement…</p>
  </div>
);

const TerminalPanel: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <p className="mt-3 text-sm leading-6 text-slate-600">{body}</p>
    <p className="mt-2 text-sm text-slate-500">
      If you believe this link should still be valid, please contact the
      business that sent it.
    </p>
  </div>
);

// ---------------------- signing flow shell ----------------------

const SigningFlow: React.FC<{
  ctx: SigningContextResponse;
  step: ScreenStep;
  token: string;
  onStepChange: (s: ScreenStep) => void;
  onTerminal: (kind: "gone" | "already-signed") => void;
  onSuccess: (r: SignatureCompletedResponse) => void;
}> = ({ ctx, step, token, onStepChange, onTerminal, onSuccess }) => {
  return (
    <div className="space-y-6">
      <StepIndicator step={step} />
      {step === "disclosure" && (
        <DisclosureStep
          disclosure={ctx.consumerRightsDisclosure}
          onContinue={() => onStepChange("preview")}
        />
      )}
      {step === "preview" && (
        <PreviewStep
          contractHtml={ctx.contractTextHtml}
          onBack={() => onStepChange("disclosure")}
          onContinue={() => onStepChange("sign")}
        />
      )}
      {step === "sign" && (
        <SignStep
          ctx={ctx}
          token={token}
          onBack={() => onStepChange("preview")}
          onTerminal={onTerminal}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};

const StepIndicator: React.FC<{ step: ScreenStep }> = ({ step }) => {
  const steps: { key: ScreenStep; label: string }[] = [
    { key: "disclosure", label: "Your rights" },
    { key: "preview", label: "Review" },
    { key: "sign", label: "Sign" },
  ];
  const currentIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-1.5 text-xs sm:text-sm">
      {steps.map((s, i) => {
        const active = i === currentIdx;
        const done = i < currentIdx;
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={[
                "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                active
                  ? "bg-blue-600 text-white"
                  : done
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-200 text-slate-500",
              ].join(" ")}
            >
              {i + 1}
            </span>
            <span
              className={
                active ? "font-medium text-slate-900" : "text-slate-500"
              }
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span
                className="mx-1 h-px w-5 bg-slate-200 sm:mx-2 sm:w-10"
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
};

// ---------------------- step 1: disclosure ----------------------

const DisclosureStep: React.FC<{
  disclosure: string;
  onContinue: () => void;
}> = ({ disclosure, onContinue }) => {
  const [acked, setAcked] = useState(false);

  // Split on blank lines and render paragraphs without dangerouslySetInnerHTML.
  const paragraphs = useMemo(() => {
    const normalized = (disclosure ?? "").replace(/\r\n/g, "\n");
    return normalized
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [disclosure]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">
          Consumer Credit File Rights Under State and Federal Law
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Federal law requires that you receive and review the disclosure below
          before signing.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 max-h-[420px] overflow-y-auto">
        {paragraphs.length === 0 ? (
          <p className="text-sm text-slate-500">
            Disclosure unavailable. Please contact the business that sent you
            this link.
          </p>
        ) : (
          paragraphs.map((p, i) => (
            <p
              key={i}
              className="text-sm leading-7 text-slate-700 whitespace-pre-line [&:not(:first-child)]:mt-4"
            >
              {p}
            </p>
          ))
        )}
      </div>

      <label className="flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={acked}
          onChange={(e) => setAcked(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
        />
        <span>
          I have received and read the Consumer Credit File Rights Under State
          and Federal Law disclosure above.
        </span>
      </label>

      <button
        type="button"
        onClick={onContinue}
        disabled={!acked}
        className={[
          "inline-flex w-full items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium text-white sm:w-auto",
          acked
            ? "bg-blue-600 hover:bg-blue-700"
            : "bg-slate-300 cursor-not-allowed",
        ].join(" ")}
      >
        Continue
      </button>
    </div>
  );
};

// ---------------------- step 2: preview ----------------------

const PreviewStep: React.FC<{
  contractHtml: string;
  onBack: () => void;
  onContinue: () => void;
}> = ({ contractHtml, onBack, onContinue }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-6">
    <div>
      <h2 className="text-lg font-semibold text-slate-900">
        Review your agreement
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Read the full agreement below. The next step will collect your
        signature.
      </p>
    </div>

    <div
      className="iw-contract-prose rounded-lg border border-slate-200 bg-white p-5 max-h-[520px] overflow-y-auto text-sm leading-7 text-slate-800"
      dangerouslySetInnerHTML={{ __html: contractHtml || "" }}
    />

    <div className="flex flex-col-reverse gap-2 sm:flex-row">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:w-auto"
      >
        Back
      </button>
      <button
        type="button"
        onClick={onContinue}
        className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 sm:w-auto"
      >
        Continue to sign
      </button>
    </div>
  </div>
);

// ---------------------- step 3: sign ----------------------

type SignTab = "type" | "draw";

const SignStep: React.FC<{
  ctx: SigningContextResponse;
  token: string;
  onBack: () => void;
  onTerminal: (kind: "gone" | "already-signed") => void;
  onSuccess: (r: SignatureCompletedResponse) => void;
}> = ({ ctx, token, onBack, onTerminal, onSuccess }) => {
  const allowed = ctx.allowedSignatureMethod;
  const initialTab: SignTab =
    allowed === 1 ? "draw" : "type"; // Drawn-only → draw; Typed-only or Either → type
  const [tab, setTab] = useState<SignTab>(initialTab);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const showType = allowed === 0 || allowed === 2;
  const showDraw = allowed === 1 || allowed === 2;

  // Type-tab state
  const [typedName, setTypedName] = useState("");
  const [typedAck, setTypedAck] = useState(false);

  // Draw-tab state
  const padRef = useRef<SignatureCanvas | null>(null);
  const padContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [drawAck, setDrawAck] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(600);

  useEffect(() => {
    const el = padContainerRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.max(280, Math.floor(el.getBoundingClientRect().width));
      setCanvasWidth(w);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleClear = () => {
    padRef.current?.clear();
    setHasStrokes(false);
  };

  const doSubmit = async (req: SubmitSignatureRequest) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await publicContractsApi.submitSignature(token, req);
      onSuccess(result);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 410) {
        onTerminal("gone");
        return;
      }
      if (status === 409) {
        onTerminal("already-signed");
        return;
      }
      const data = err?.response?.data;
      const msg =
        (typeof data === "string" ? data : null) ??
        data?.error ??
        data?.message ??
        data?.title ??
        err?.message ??
        "We couldn't accept your signature. Please check your input and try again.";
      setSubmitError(typeof msg === "string" ? msg : String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitTyped = () => {
    if (typedName.trim().length < 2 || !typedAck) return;
    doSubmit({ methodUsed: 0, typedName: typedName.trim() });
  };

  const handleSubmitDrawn = () => {
    if (!padRef.current || padRef.current.isEmpty() || !drawAck) return;
    let dataUrl = "";
    try {
      dataUrl = padRef.current.getTrimmedCanvas().toDataURL("image/png");
    } catch {
      dataUrl = padRef.current.toDataURL("image/png");
    }
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, "");
    doSubmit({ methodUsed: 1, drawnPngBase64: base64 });
  };

  const typedReady = typedName.trim().length >= 2 && typedAck && !submitting;
  const drawnReady = hasStrokes && drawAck && !submitting;

  const heading =
    allowed === 0
      ? "Type your signature"
      : allowed === 1
      ? "Draw your signature"
      : "Type or draw your signature";

  return (
    <div className="space-y-6">
      {/* For-reference contract content sits above; on mobile the panel below floats */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm pb-4 sm:pb-8">
        <h2 className="text-lg font-semibold text-slate-900">{heading}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Your signature confirms that you've read and agree to the terms of
          this agreement.
        </p>
        <details className="mt-4 group">
          <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-800">
            Show agreement for reference
          </summary>
          <div
            className="iw-contract-prose mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 max-h-[280px] overflow-y-auto text-xs leading-6 text-slate-700"
            dangerouslySetInnerHTML={{ __html: ctx.contractTextHtml || "" }}
          />
        </details>
      </div>

      {/* Sticky-bottom signing panel on mobile, inline card on desktop */}
      <div className="fixed bottom-0 inset-x-0 z-10 bg-white border-t border-slate-200 shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.25)] sm:relative sm:bottom-auto sm:inset-auto sm:border sm:border-slate-200 sm:rounded-2xl sm:shadow-sm">
        <div className="mx-auto w-full max-w-3xl px-5 py-5 sm:px-6 sm:py-6 space-y-5">
          {/* tabs (only when both methods allowed) */}
          {showType && showDraw && (
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
              <TabButton
                active={tab === "type"}
                onClick={() => setTab("type")}
                label="Type"
              />
              <TabButton
                active={tab === "draw"}
                onClick={() => setTab("draw")}
                label="Draw"
              />
            </div>
          )}

          {tab === "type" && showType && (
            <TypeSignaturePanel
              typedName={typedName}
              onTypedNameChange={setTypedName}
              ack={typedAck}
              onAckChange={setTypedAck}
            />
          )}

          {tab === "draw" && showDraw && (
            <DrawSignaturePanel
              padRef={padRef}
              padContainerRef={padContainerRef}
              canvasWidth={canvasWidth}
              onEnd={() => {
                if (padRef.current)
                  setHasStrokes(!padRef.current.isEmpty());
              }}
              onClear={handleClear}
              ack={drawAck}
              onAckChange={setDrawAck}
            />
          )}

          {submitError && (
            <div
              role="alert"
              className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
            >
              {submitError}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:w-auto"
            >
              Back
            </button>
            <button
              type="button"
              onClick={tab === "type" ? handleSubmitTyped : handleSubmitDrawn}
              disabled={tab === "type" ? !typedReady : !drawnReady}
              className={[
                "inline-flex w-full items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium text-white sm:w-auto",
                (tab === "type" ? typedReady : drawnReady)
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-slate-300 cursor-not-allowed",
              ].join(" ")}
            >
              {submitting ? "Signing…" : "Sign agreement"}
            </button>
          </div>
        </div>
      </div>

      {/* Spacer so sticky panel doesn't cover the reference content on mobile */}
      <div className="h-[280px] sm:hidden" aria-hidden />
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
}> = ({ active, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
      active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900",
    ].join(" ")}
  >
    {label}
  </button>
);

const TypeSignaturePanel: React.FC<{
  typedName: string;
  onTypedNameChange: (v: string) => void;
  ack: boolean;
  onAckChange: (v: boolean) => void;
}> = ({ typedName, onTypedNameChange, ack, onAckChange }) => (
  <div className="space-y-4">
    <div>
      <label
        htmlFor="iw-typed-name"
        className="block text-sm font-medium text-slate-700"
      >
        Full legal name
      </label>
      <input
        id="iw-typed-name"
        type="text"
        autoComplete="name"
        value={typedName}
        onChange={(e) => onTypedNameChange(e.target.value)}
        maxLength={200}
        placeholder="e.g. Jane Marie Doe"
        className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
      />
    </div>

    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 min-h-[88px] flex items-center justify-center">
      {typedName.trim().length === 0 ? (
        <span className="text-xs text-slate-400">Signature preview</span>
      ) : (
        <span
          className="text-3xl sm:text-4xl text-slate-900 leading-none"
          style={{
            fontFamily: "'Great Vibes', cursive",
          }}
        >
          {typedName}
        </span>
      )}
    </div>

    <label className="flex items-start gap-3 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={ack}
        onChange={(e) => onAckChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
      />
      <span>
        I intend this typed name to be my legal electronic signature.
      </span>
    </label>
  </div>
);

const DrawSignaturePanel: React.FC<{
  padRef: React.RefObject<SignatureCanvas | null>;
  padContainerRef: React.RefObject<HTMLDivElement | null>;
  canvasWidth: number;
  onEnd: () => void;
  onClear: () => void;
  ack: boolean;
  onAckChange: (v: boolean) => void;
}> = ({ padRef, padContainerRef, canvasWidth, onEnd, onClear, ack, onAckChange }) => (
  <div className="space-y-4">
    <div ref={padContainerRef} className="w-full">
      <SignatureCanvas
        ref={padRef}
        onEnd={onEnd}
        canvasProps={{
          width: canvasWidth,
          height: 200,
          className:
            "block w-full rounded-lg border border-slate-300 bg-white touch-none",
        }}
      />
    </div>
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">
        Sign with your finger, stylus, or mouse.
      </span>
      <button
        type="button"
        onClick={onClear}
        className="text-sm font-medium text-slate-600 hover:text-slate-900"
      >
        Clear
      </button>
    </div>

    <label className="flex items-start gap-3 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={ack}
        onChange={(e) => onAckChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
      />
      <span>
        I intend this drawing to be my legal electronic signature.
      </span>
    </label>
  </div>
);

// ---------------------- completed ----------------------

const CompletedPanel: React.FC<{
  ctx: SigningContextResponse;
  result: SignatureCompletedResponse;
  token: string;
}> = ({ ctx, result, token }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const fileName = useMemo(() => {
    const slug = (ctx.clientFullName || "service-agreement")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return `service-agreement-${slug || "signed"}.pdf`;
  }, [ctx.clientFullName]);

  const signedAt = useMemo(() => {
    try {
      return new Date(result.signedAt).toLocaleString();
    } catch {
      return result.signedAt;
    }
  }, [result.signedAt]);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await publicContractsApi.downloadSignedPdf(token, fileName);
    } catch (err: any) {
      setDownloadError(
        err?.message ?? "Couldn't download the PDF. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
          aria-hidden
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-900">
        Agreement signed
      </h2>
      <p className="mt-2 text-sm text-slate-600">Signed {signedAt}</p>
      <p className="mt-1 text-xs text-slate-500">
        Verification ID {result.integrityHashSha256Prefix}
      </p>

      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="mt-6 inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
      >
        {downloading ? "Preparing…" : "Download your copy"}
      </button>

      {downloadError && (
        <p className="mt-3 text-sm text-rose-600">{downloadError}</p>
      )}

      <p className="mt-6 text-xs text-slate-500">
        A copy has also been emailed to you.
      </p>
    </div>
  );
};

export default SignContractPage;
